import { CONCEPT_ICONS } from "@/components/ui/concept-icons";
/**
 * Permission card — floating overlay for directory access consent.
 *
 * Rendered when the companion detects that project directories are
 * not accessible (macOS TCC / file permission restrictions).
 * Shows the blocked paths and lets the user retry or skip.
 */

import { useState, useCallback, useEffect } from "react";
import { useT } from "@/i18n";
import { canvasMessages } from "@/i18n/messages/canvas";
import { cn } from "@/lib/cn";
import { Button } from "@/components/ui";
import { useCompanionStore } from "@/stores/companion-store";
import { useCanvasStore } from "@/stores/canvas-store";
import {
  surfaceRoleAttributes,
  surfaceRoleClassName,
} from "@/components/ui/surface-contract";

interface PermissionCardProps {
  executionId: string;
  blockedDirectories: string[];
  className?: string;
}

export function PermissionCard({
  executionId,
  blockedDirectories,
  className,
}: PermissionCardProps) {
  const t = useT(canvasMessages);
  const [pending, setPending] = useState<"retry" | "skip" | null>(null);
  const sendPermissionResolved = useCompanionStore(
    (s) => s.sendPermissionResolved,
  );
  const removePendingPermission = useCanvasStore(
    (s) => s.removePendingPermission,
  );

  // Reset pending state when blocked directories change (companion
  // re-sent permission.directory_required after a retry that still failed).
  useEffect(() => {
    setPending(null);
  }, [blockedDirectories]);

  const handleRetry = useCallback(() => {
    if (!sendPermissionResolved) return;
    setPending("retry");
    sendPermissionResolved(executionId, false);
    // Don't remove from store — the companion will either re-send
    // permission.directory_required (still blocked, which re-renders
    // the card and resets pending) or start the execution (which
    // clears the card via removePendingPermission).
  }, [executionId, sendPermissionResolved]);

  const handleSkip = useCallback(() => {
    if (!sendPermissionResolved) return;
    setPending("skip");
    sendPermissionResolved(executionId, true);
    removePendingPermission(executionId);
  }, [executionId, sendPermissionResolved, removePendingPermission]);

  const isDisabled = pending !== null;

  return (
    <div
      className={cn(
        surfaceRoleClassName("menu"),
        "w-[420px] p-4 flex flex-col gap-3",
        className,
      )}
      data-testid="permission-card"
      {...surfaceRoleAttributes("menu", {
        nativeLevel: "root",
        nativeGroup: "menus",
      })}
    >
      {/* Header */}
      <div className="flex items-start gap-2">
        <div className="mt-0.5 rounded-control p-1.5">
          <CONCEPT_ICONS.approval className="h-4 w-4 text-warning" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-canvas-base font-semibold text-muted uppercase tracking-wide">
            {t("directoryAccessRequired")}
          </p>
          <p className="text-canvas-md font-semibold text-primary mt-0.5">
            {t("directoriesNeedPermission", {
              count: blockedDirectories.length,
            })}
          </p>
        </div>
      </div>

      {/* Blocked directories */}
      <div className="flex flex-col gap-1.5">
        {blockedDirectories.map((dir) => (
          <div
            key={dir}
            className="flex items-center gap-2 rounded-control bg-glass-fill-heavy px-2.5 py-1.5"
            data-testid="permission-card-blocked-dir"
          >
            <CONCEPT_ICONS.folder className="h-3.5 w-3.5 shrink-0 text-muted" />
            <code className="text-appkit-small text-secondary truncate">
              {dir}
            </code>
          </div>
        ))}
      </div>

      {/* Instructions */}
      <p className="text-appkit-small text-muted leading-relaxed">
        {t.rich("grantAccess", {
          b: (chunks) => (
            <span className="text-secondary font-medium">{chunks}</span>
          ),
        })}
      </p>

      {/* Actions */}
      <div className="flex items-center justify-end gap-2 pt-1">
        <Button
          variant="ghost"
          size="sm"
          disabled={isDisabled}
          onClick={handleSkip}
          data-testid="permission-card-skip-btn"
        >
          {pending === "skip" ? t("skipping") : t("skipDirectories")}
        </Button>
        <Button
          variant="default"
          size="sm"
          disabled={isDisabled}
          onClick={handleRetry}
          data-testid="permission-card-retry-btn"
        >
          {pending === "retry" ? t("checking") : t("retry")}
        </Button>
      </div>
    </div>
  );
}
